import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";

export type BadgeTone =
  | "neutral"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "primary"
  | "reads"
  | "kitchen"
  | "house"
  | "sun"
  | "solid";

export interface BadgeProps {
  tone?: BadgeTone;
  /** Leading dot in the badge's colour, so tone is not carried by colour alone. */
  dot?: boolean;
  /** Small uppercase label style (craft tags). */
  caps?: boolean;
  children: ReactNode;
  className?: string;
}

/** Read-only status. Use Chip for anything the user can press. */
export function Badge({ tone = "neutral", dot = false, caps = false, children, className }: BadgeProps) {
  return (
    <span className={cx("ui-badge", tone !== "neutral" && `ui-badge--${tone}`, caps && "ui-badge--caps", className)}>
      {dot && <span className="ui-badge__dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Toggle state. Rendered as aria-pressed, which also drives the style. */
  selected?: boolean;
  size?: "sm" | "md";
  icon?: ReactNode;
}

/** A selectable filter or choice. */
export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { selected, size = "md", icon, className, children, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={selected === undefined ? undefined : selected}
      className={cx("ui-chip", size === "sm" && "ui-chip--sm", className)}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});
